import { FieldConfig } from '../DynamicForm/types'
import { SelectOption } from '../StruxhubInputs/StruxhubSearchableSelect/StruxhubSearchableSelect'

/**
 * The fields the edit form should show: every required field (e.g. the static
 * `name`), every field the resource actually has (a key present in
 * `record.data`, whether filled or null/empty), plus any field the user has
 * revealed via "Add field" this session. Assigned fields are ordered by their
 * data-key position — the order they were added — independent of the creation
 * form's field order. A resource's `data` preserves insertion order and saving
 * appends newly-added keys, so the order stays stable across saves. Fields the
 * resource has no key for at all stay hidden and are offered in "Add field".
 */
export const computeVisibleFields = (
  fields: FieldConfig[],
  recordData: Record<string, unknown> | null | undefined,
  revealedFieldNames: ReadonlySet<string>,
): FieldConfig[] => {
  const dataKeys = recordData ? Object.keys(recordData) : []
  const dataOrder = new Map(dataKeys.map((key, i) => [key, i]))

  // Stable sort (ES2019+): required fields (e.g. name) first, then always-visible
  // system fields (color/icon/parent) in catalog order, then assigned fields by
  // data-key position. Ranks 0/1 aren't in dataOrder, so they tie at Infinity
  // and keep their catalog order.
  const rank = (field: FieldConfig): number =>
    field.required === true ? 0 : field.alwaysVisible === true ? 1 : 2

  const base = fields
    .filter(
      field =>
        field.required === true ||
        field.alwaysVisible === true ||
        dataOrder.has(field.name),
    )
    .sort((a, b) => {
      const ra = rank(a)
      const rb = rank(b)
      if (ra !== rb) return ra - rb
      return (
        (dataOrder.get(a.name) ?? Infinity) -
        (dataOrder.get(b.name) ?? Infinity)
      )
    })

  const baseNames = new Set(base.map(field => field.name))

  // Revealed fields render after the existing ones, in the order they were
  // added (a Set preserves insertion order), so a newly added field always
  // sits below all other fields and above the "Add field" control.
  const revealed: FieldConfig[] = []
  revealedFieldNames.forEach(name => {
    if (baseNames.has(name)) return
    const field = fields.find(f => f.name === name)
    if (field) revealed.push(field)
  })

  return [...base, ...revealed]
}

/**
 * Options for the "Add field" dropdown: catalog fields that are not currently
 * visible and are genuinely addable (not read-only/computed/etc., which arrive
 * already flagged `readOnly` on the FieldConfig).
 *
 * Sorted by label so the list is scannable — the catalog order it would
 * otherwise inherit is the resource's seeded property order, which reads as
 * arbitrary once a project has added properties of its own.
 */
export const computeAddableFieldOptions = (
  catalogFields: FieldConfig[],
  visibleFieldNames: ReadonlySet<string>,
): SelectOption[] =>
  catalogFields
    .filter(
      field =>
        !field.readOnly &&
        !field.alwaysVisible &&
        !visibleFieldNames.has(field.name),
    )
    .map(field => ({ value: field.name, label: field.label || field.name }))
    .sort((a, b) => a.label.localeCompare(b.label))
